import { useDateFormatter, useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import { Button } from 'components/lib';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import React from 'react';
import { CreateRFI } from 'records/interactions/CreateRFI';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import LoadingOverlay from '../../LoadingOverlay';
import OverlayFooter from '../../OverlayFooter';
import OverlayHeader from '../../OverlayHeader';
import OfficialQuestionRFIOverlay from './OfficialQuestionSection';
import ProjectDetailsSection from './ProjectDetailsSection';

interface Props extends WithAppStoreProps {
  interaction: CreateRFI;
  onToggleExpand: () => void;
  onCancelClick: () => void;
  onCreateRFI: () => Promise<void>;
  onSaveDraft: () => Promise<void>;
  onDeleteDraft: (evt: React.MouseEvent<HTMLElement>) => void;
  saveDisabled: boolean;
  deleteDisabled: boolean;
  creatingRFI: boolean;
  creatingText: string;
}

const NarrowCreateRFIOverlay = (props: Props) => {
  const {
    interaction,
    appState,
    saveDisabled,
    deleteDisabled,
    onCancelClick,
    onToggleExpand,
    onCreateRFI,
    onSaveDraft,
    onDeleteDraft,
    creatingRFI,
    creatingText,
  } = props;
  const { userId } = useAuth();

  const { projects, selectedProject, users } = appState;
  const currentUser = users.find((u) => userId === u.id);

  const project = projects.get(selectedProject);

  if (!project || !currentUser) {
    return null;
  }

  const submittingName = `${currentUser.profile.firstname} ${currentUser.profile.lastname}`;

  const hasSubmittingCompany = currentUser.profile.company.length > 0;
  const hasSubmittingName =
    `${currentUser.profile.firstname}${currentUser.profile.lastname}`.length >
    0;
  const hasSubmittingEmail = currentUser.email.length > 0;

  const { translate } = useTranslator(['formLabels', 'buttons']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <OverlayStdContainer
      id="create-rfi-overlay"
      style={{ backgroundColor: 'var(--white-2)' }}
    >
      {creatingRFI ? <LoadingOverlay text={creatingText} /> : null}
      <OverlayHeader
        id="create-rfi-overlay-header"
        title={translate('rfiPage:newRFI')}
        onCancelClick={onCancelClick}
        showExpand
        expanded={false}
        onToggleExpand={onToggleExpand}
      />
      <div className="column justify-between oa grow-1">
        <div className="my2 mx3">
          <OfficialQuestionRFIOverlay interaction={interaction} />

          <OverlaySection label={translate('formLabels:requestedBy')}>
            {hasSubmittingName ? <div>{submittingName}</div> : null}
            {hasSubmittingEmail ? <div>{currentUser.email}</div> : null}
            {hasSubmittingCompany ? (
              <div>{currentUser.profile.company}</div>
            ) : null}
            <div>{dateFormatter.format(new Date())}</div>
          </OverlaySection>
          <ProjectDetailsSection interaction={interaction} />
        </div>
      </div>
      <OverlayFooter
        id="create-rfi-overlay-overlay-footer"
        confirmText={translate('buttons:send')}
        onConfirmClick={onCreateRFI}
        confirmDisabled={saveDisabled}
        onCancelClick={onCancelClick}
        hideCancel={!deleteDisabled}
        hideSecondary={deleteDisabled}
        secondaryText={translate('buttons:save')}
        secondaryDisabled={saveDisabled}
        onSecondaryClick={onSaveDraft}
      >
        {deleteDisabled ? (
          <Button
            id="create-rfi-overlay-overlay-footer-save-draft-btn"
            variant="secondary"
            size="large"
            disabled={saveDisabled}
            onClick={onSaveDraft}
          >
            {translate('buttons:save')}
          </Button>
        ) : (
          <Button
            id="create-rfi-overlay-overlay-footer-delete-draft-btn"
            variant="destructive"
            size="large"
            disabled={deleteDisabled}
            onClick={onDeleteDraft}
          >
            {translate('rfiPage:deleteDraft')}
          </Button>
        )}
      </OverlayFooter>
    </OverlayStdContainer>
  );
};

export default withAppStore<Props>(NarrowCreateRFIOverlay);
